import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Users, UserCog, BarChart3, ClipboardList, Flag, Building2 } from 'lucide-react';

export default function CoordinatorDashboard() {
  const { axiosAuth } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [clients, setClients] = useState([]);
  const [flags, setFlags] = useState([]);
  const [notes, setNotes] = useState([]);
  const navigate = useNavigate();
  const api = axiosAuth();

  useEffect(() => {
    api.get('/analytics/overview').then(r => setAnalytics(r.data)).catch(() => {});
    api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
    api.get('/flags').then(r => setFlags(r.data)).catch(() => {});
    api.get('/session-notes').then(r => setNotes(r.data)).catch(() => {});
  }, []);

  const activeFlags = flags.filter(f => !f.resolved);
  const pendingNotes = notes.filter(n => n.status === 'draft');

  return (
    <div className="space-y-6" data-testid="coordinator-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>Program Coordinator</h1>
        <p className="text-muted-foreground mt-1 text-sm">Program overview and management</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: 'Total Clients', value: analytics?.total_clients || 0, icon: Users, color: 'teal', route: '/clients?status=active', testId: 'coord-stat-total-clients' },
          { label: 'Specialists', value: analytics?.total_specialists || 0, icon: UserCog, color: 'sky', route: '/users?role=peer_specialist', testId: 'coord-stat-specialists' },
          { label: 'Active Flags', value: activeFlags.length, icon: Flag, color: 'red', route: '/flags?status=active', testId: 'coord-stat-active-flags' },
          { label: 'Pending Notes', value: pendingNotes.length, icon: ClipboardList, color: 'amber', route: '/session-notes?status=draft', testId: 'coord-stat-pending-notes' },
        ].map(s => (
          <Card 
            key={s.label} 
            className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(s.route)}
            data-testid={s.testId}
          >
            <CardContent className="p-4">
              <div className={`w-8 h-8 rounded-lg bg-${s.color}-50 flex items-center justify-center mb-2`}><s.icon className={`w-4 h-4 text-${s.color}-600`} /></div>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Program Metrics</CardTitle>
            <Button variant="ghost" size="sm" className="text-teal-600 text-xs" onClick={() => navigate('/analytics')} data-testid="coord-full-report">Full Report</Button>
          </CardHeader>
          <CardContent>
            {analytics ? (
              <div className="space-y-3">
                <div 
                  className="flex justify-between p-2 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => navigate('/checkins')}
                  data-testid="coord-metric-checkins"
                >
                  <span className="text-sm text-muted-foreground">Total Check-ins</span>
                  <span className="text-sm font-medium">{analytics.total_checkins}</span>
                </div>
                <div 
                  className="flex justify-between p-2 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => navigate('/appointments')}
                  data-testid="coord-metric-appointments"
                >
                  <span className="text-sm text-muted-foreground">Total Appointments</span>
                  <span className="text-sm font-medium">{analytics.total_appointments}</span>
                </div>
                <div 
                  className="flex justify-between p-2 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => navigate('/session-notes')}
                  data-testid="coord-metric-session-notes"
                >
                  <span className="text-sm text-muted-foreground">Session Notes</span>
                  <span className="text-sm font-medium">{analytics.total_sessions}</span>
                </div>
                <div 
                  className="flex justify-between p-2 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => navigate('/analytics')}
                  data-testid="coord-metric-mood"
                >
                  <span className="text-sm text-muted-foreground">Avg Mood Score</span>
                  <span className="text-sm font-medium">{analytics.avg_mood}/10</span>
                </div>
                <div 
                  className="flex justify-between p-2 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => navigate('/analytics')}
                  data-testid="coord-metric-cravings"
                >
                  <span className="text-sm text-muted-foreground">Avg Cravings</span>
                  <span className="text-sm font-medium">{analytics.avg_cravings}/10</span>
                </div>
              </div>
            ) : <p className="text-sm text-muted-foreground">Loading metrics...</p>}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Active Flags</CardTitle>
            <Button variant="ghost" size="sm" className="text-teal-600 text-xs" onClick={() => navigate('/flags')} data-testid="coord-view-flags">View all</Button>
          </CardHeader>
          <CardContent>
            {activeFlags.length > 0 ? (
              <div className="space-y-2">
                {activeFlags.slice(0, 5).map(f => (
                  <div 
                    key={f.id} 
                    className="flex items-center gap-3 p-2.5 rounded-xl bg-red-50/50 cursor-pointer hover:bg-red-100/50 transition-colors"
                    onClick={() => navigate('/flags')}
                    data-testid={`coord-flag-${f.id}`}
                  >
                    <Flag className="w-4 h-4 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{f.reason}</p>
                      <p className="text-xs text-muted-foreground">By {f.flagged_by_name}</p>
                    </div>
                    <Badge className={f.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} variant="secondary">{f.severity}</Badge>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No active flags.</p>}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-teal-50" onClick={() => navigate('/clients')} data-testid="coord-manage-clients">
              <Users size={18} className="text-teal-600" /><span className="text-xs">Manage Clients</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-sky-50" onClick={() => navigate('/session-notes')}>
              <ClipboardList size={18} className="text-sky-600" /><span className="text-xs">Review Notes</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-amber-50" onClick={() => navigate('/analytics')}>
              <BarChart3 size={18} className="text-amber-600" /><span className="text-xs">Analytics</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-violet-50" onClick={() => navigate('/group-sessions')}>
              <Building2 size={18} className="text-violet-600" /><span className="text-xs">Group Sessions</span>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
